import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { isNullOrEmpty } from "../../utils/string";
import { ResourceCache } from "./ResourceCache";
import { KEY_IS_FALLBACK } from "./internal/GeometrySerialization";
import { NodeUserData } from "./NodeUserData";
export class GeometryManager {
    get scene() {
        return (this.mScene);
    }
    constructor(pModelManager) {
        this.onDisposeMesh = (pMesh, pCacheCleared) => {
            if (pMesh == null)
                return;
            pMesh.dispose();
        };
        this.mScene = pModelManager.scene;
        this.mMeshCache = new ResourceCache();
        this.mMeshCache.onDisposeResource = this.onDisposeMesh;
        this.mGeom2dCache = new ResourceCache();
        this.mLastCacheCleanUp = Date.now();
        this.missingGeometries = new Set();
    }
    clear() {
        this.mLastCacheCleanUp = Date.now();
        this.mMeshCache.clear();
        this.mGeom2dCache.clear();
        this.missingGeometries.clear();
    }
    addGeometryMesh(pId, pGeometryMesh) {
        if (isNullOrEmpty(pId) || pGeometryMesh == null || pGeometryMesh.geometry == null)
            return (false);
        const tGeom = this.getGeometry(pId);
        if (tGeom == null || this.isFallbackGeometry(tGeom)) {
            //dispose fallback geometry
            if (tGeom != null)
                this.mMeshCache.remove(pId, true);
            this.mMeshCache.add(pId, pGeometryMesh);
            //force correct id
            pGeometryMesh.id = pId;
            pGeometryMesh.geometry.id = pId;
            this.mScene.removeMesh(pGeometryMesh); //remove from scene, only keep in cache
            //remove from missing list
            this.missingGeometries.delete(pId);
            return (true);
        }
        return (false);
    }
    getGeometryMesh(pGeomId) {
        if (isNullOrEmpty(pGeomId))
            return (null);
        //check cache
        const tMesh = this.mMeshCache.get(pGeomId);
        if (tMesh == null || tMesh.geometry == null)
            return (null);
        return (tMesh);
    }
    getGeometry(pId) {
        const tMesh = this.getGeometryMesh(pId);
        if (tMesh != null)
            return (tMesh.geometry);
        else
            return (null);
    }
    hasGeometry(pId) {
        if (isNullOrEmpty(pId))
            return (false);
        return (this.mMeshCache.has(pId));
    }
    isFallbackGeometry(pGeometry) {
        if (pGeometry == null)
            return (false);
        return (pGeometry[KEY_IS_FALLBACK] === true);
    }
    getMeshGeometryId(pMesh) {
        if (!(pMesh instanceof Mesh) || pMesh.geometry == null)
            return (null);
        if (!this.hasGeometry(pMesh.geometry.id))
            return (null);
        return (pMesh.geometry.id);
    }
    addGeometry2d(pId, pBuffer) {
        if (isNullOrEmpty(pId) || this.hasGeometry2d(pId))
            return (false);
        this.mGeom2dCache.add(pId, pBuffer);
        return (true);
    }
    getGeometry2d(pId) {
        return (this.mGeom2dCache.get(pId));
    }
    hasGeometry2d(pId) {
        return (this.mGeom2dCache.has(pId));
    }
    getNodeGeometry2dId(pNode) {
        return NodeUserData.GetData(pNode, GeometryManager.NODEDATA_GEOMETRY2D);
    }
    setNodeGeometry2dId(pNode, pId) {
        NodeUserData.SetData(pNode, GeometryManager.NODEDATA_GEOMETRY2D, pId);
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    _cleanUpCache(pImmediately) {
        const tNow = Date.now();
        if (pImmediately || tNow - this.mLastCacheCleanUp > GeometryManager.CACHE_CLEANUP_INTERVAL) {
            this.mLastCacheCleanUp = tNow;
            this.cleanUpMeshCache();
            this.cleanUpGeom2DCache();
        }
    }
    cleanUpMeshCache() {
        //collect used geometries
        const tUsedGeoms = new Set();
        this.mMeshCache.forEach((pId, pMesh) => {
            if (pMesh != null && pMesh.geometry != null) {
                if (pMesh.geometry.meshes.length > 1) {
                    tUsedGeoms.add(pId);
                }
            }
            else {
                tUsedGeoms.add(pId); //do not reload empty geometries
            }
        });
        //remove unused
        this.mMeshCache.removeUnused(tUsedGeoms, GeometryManager.CACHE_CLEANUP_INTERVAL);
    }
    cleanUpGeom2DCache() {
        //collect used geometries
        const tUsedGeoms = new Set();
        for (const tMesh of this.mScene.meshes) {
            const tGeomId = this.getNodeGeometry2dId(tMesh);
            if (tGeomId != null) {
                tUsedGeoms.add(tGeomId);
            }
        }
        for (const tNode of this.mScene.transformNodes) {
            const tGeomId = this.getNodeGeometry2dId(tNode);
            if (tGeomId != null) {
                tUsedGeoms.add(tGeomId);
            }
        }
        //remove unused
        this.mGeom2dCache.removeUnused(tUsedGeoms, GeometryManager.CACHE_CLEANUP_INTERVAL);
    }
}
//// private ///////////////////////////////////////////////////////////////////////////////
GeometryManager.CACHE_CLEANUP_INTERVAL = 120000;
GeometryManager.NODEDATA_GEOMETRY2D = "core:geometry2d";
